import { useMemo } from 'react'

import { Tick02Icon } from '@hugeicons/react'

import { Button } from '@/shared/ui/Button'
import { Link } from '@/shared/ui/Link'

const baseSuggestions = [
  'Atualize a página para tentar novamente',
  'Verifique sua conexão com a internet',
  'Entre em contato com o suporte se o problema persistir'
]

export const ErrorScreen = ({
  statusCode,
  title,
  description,
  details = [],
  suggestions = baseSuggestions,
  actionHref = '/',
  actionLabel = 'Voltar para o início',
  illustration = '/misc/gamers.png',
  errorCode
}) => {
  const timestamp = useMemo(() => {
    try {
      return new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'medium'
      }).format(new Date())
    } catch (error) {
      return new Date().toLocaleString()
    }
  }, [])

  const computedDetails = useMemo(() => {
    const list = [...details]

    if (statusCode) list.unshift({ label: 'Código HTTP', value: statusCode })
    if (errorCode)
      list.push({
        label: 'Identificador do erro',
        value: errorCode
      })

    list.push({ label: 'Horário do evento', value: timestamp })

    return list
  }, [details, errorCode, statusCode, timestamp])

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="flex flex-col items-center text-center p-10 w-full">
        <div className="w-full lg:max-w-[650px] shadow-sm rounded-2xl bg-white">
          <div className="py-15 px-4 sm:px-15">
            <div className="mb-5">
              <div className="flex items-center justify-center w-[100px] h-[100px] rounded-full bg-brand/10 self-center mb-7 mx-auto">
                <span className="text-4xl font-bold text-brand">
                  {statusCode || 'Ooops'}
                </span>
              </div>
              <h1 className="font-extrabold text-4xl text-gray-900 mb-4">{title}</h1>
              <div className="font-semibold text-sm text-gray-700 mb-6">
                {description}
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 justify-center mb-10">
              {computedDetails.map(detail => (
                <div
                  key={`${detail.label}-${detail.value}`}
                  className="grow px-5 py-4 bg-brand/10 rounded-xl border border-brand/20"
                >
                  <div className="font-semibold text-gray-800 uppercase text-xs mb-1">
                    {detail.label}
                  </div>
                  <div className="font-bold text-gray-900 text-sm break-all">
                    {detail.value}
                  </div>
                </div>
              ))}
            </div>

            {Array.isArray(suggestions) && suggestions.length > 0 && (
              <div className="mb-10">
                <div className="font-bold text-gray-800 mb-3">
                  Algumas sugestões rápidas
                </div>
                <ul className="list-none grid gap-2 text-sm text-gray-700 p-0 m-0">
                  {suggestions.map(suggestion => (
                    <li
                      key={suggestion}
                      className="px-4 py-3 bg-gray-50 rounded-xl border border-dashed border-gray-300 text-left"
                    >
                      <Tick02Icon variant="solid" size={16} className="text-success mr-2" />
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mb-10">
              <img
                src={illustration}
                alt="Ilustração de erro"
                className="max-w-full max-h-[275px] object-contain rounded-2xl shadow-sm"
              />
            </div>

            <div className="flex flex-col gap-3 items-center">
              <Button
                variant="primary"
                href={actionHref}
                showTrailingIcon={false}
                className="px-10"
              >
                {actionLabel}
              </Button>
              <div className="text-xs text-gray-700">
                Se precisar de ajuda imediata, envie um e-mail para{' '}
                <a href="mailto:suporte@evnttz.com.br" className="text-brand">
                  suporte@evnttz.com.br
                </a>
                .
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ErrorScreen
